import React, { useState, useEffect, useCallback } from "react";
import api from "@/lib/api";
import { useSearchParams } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { formatIDR, formatDate } from "@/lib/format";
import { PageHeader, Loading, EmptyState, Field, ImageUpload, SkeletonTable } from "@/components/ui-helpers";
import { ArchiveDialog } from "@/components/ArchiveDialog";
import { RowActions } from "@/components/RowActions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Receipt } from "lucide-react";
import { toast } from "sonner";

export default function Expenses() {
  const { constants, trips, selectedTripId } = useApp();
  const { isAdmin } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [archiveTarget, setArchiveTarget] = useState(null);
  const [form, setForm] = useState({ trip_id: "", category: "Lainnya", description: "", amount: "", currency: "IDR", exchange_rate: "", receipt: null, notes: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get("/expenses", { params: { trip_id: selectedTripId, include_deleted: isAdmin && showDeleted } }); setItems(r.data); }
    finally { setLoading(false); }
  }, [selectedTripId, isAdmin, showDeleted]);
  useEffect(() => { load(); }, [load]);

  const total = items.filter((e) => !e.deleted).reduce((s, e) => s + (e.amount_idr || 0), 0);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const openNew = () => { const tid = selectedTripId !== "all" ? selectedTripId : (trips[0]?.id || ""); const t = trips.find((x) => x.id === tid); setForm({ trip_id: tid, category: "Lainnya", description: "", amount: "", currency: "IDR", exchange_rate: t?.exchange_rate || "", receipt: null, notes: "" }); setOpen(true); };

  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get("new") === "1") { openNew(); searchParams.delete("new"); setSearchParams(searchParams, { replace: true }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const save = async () => {
    if (!form.trip_id) return toast.error("Pilih trip");
    if (!form.amount) return toast.error("Isi jumlah");
    try { await api.post("/expenses", form); toast.success("Pengeluaran dicatat"); setOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Gagal"); }
  };

  const doArchive = async () => {
    await api.delete(`/expenses/${archiveTarget.id}`);
    toast.success("Pengeluaran diarsipkan"); load();
  };

  return (
    <div>
      <PageHeader title="Pengeluaran" subtitle={`Total: ${formatIDR(total)}`}>
        {isAdmin && (
          <label className="flex items-center gap-2 text-sm text-muted-foreground mr-1 cursor-pointer" data-testid="expense-show-deleted">
            <Checkbox checked={showDeleted} onCheckedChange={setShowDeleted} />Tampilkan Terhapus
          </label>
        )}
        <Button onClick={openNew} data-testid="expense-create-button"><Plus className="h-4 w-4 mr-1.5" />Tambah Pengeluaran</Button>
      </PageHeader>

      {loading ? <SkeletonTable /> : items.length === 0 ? (
        <EmptyState title="Belum ada pengeluaran" action={<Button onClick={openNew}><Plus className="h-4 w-4 mr-1.5" />Tambah</Button>} />
      ) : (
        <div className="rounded-xl border border-border bg-card overflow-hidden">
          {items.map((e) => (
            <div key={e.id} className={`flex items-center gap-3 p-3.5 border-b border-border last:border-0 ${e.deleted ? "opacity-60 bg-secondary/30" : ""}`} data-testid="expense-row">
              <div className="h-9 w-9 rounded-lg bg-accent/15 text-accent-foreground flex items-center justify-center"><Receipt className="h-4 w-4" /></div>
              <div className="flex-1 min-w-0"><p className="text-sm font-medium">{e.category}{e.deleted && <span className="ml-2 text-xs text-destructive">(Terarsip)</span>}</p><p className="text-xs text-muted-foreground">{e.description} · {formatDate(e.expense_date)}</p></div>
              <div className="text-right">
                <p className="font-mono font-semibold">{formatIDR(e.amount_idr)}</p>
                {e.currency === "AUD" && <p className="text-xs text-muted-foreground">AUD {e.amount} @ {e.exchange_rate}</p>}
              </div>
              {isAdmin && !e.deleted && <RowActions onArchive={() => setArchiveTarget(e)} testId="expense" />}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Tambah Pengeluaran</DialogTitle></DialogHeader>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Trip" required>
              <Select value={form.trip_id} onValueChange={(v) => set("trip_id", v)}><SelectTrigger data-testid="expense-trip-select"><SelectValue placeholder="Pilih trip" /></SelectTrigger>
                <SelectContent>{trips.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Kategori">
              <Select value={form.category} onValueChange={(v) => set("category", v)}><SelectTrigger data-testid="expense-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>{(constants?.expense_categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
            </Field>
            <Field label="Jumlah" required><Input type="number" value={form.amount} onChange={(e) => set("amount", e.target.value)} data-testid="expense-amount-input" /></Field>
            <Field label="Mata Uang">
              <Select value={form.currency} onValueChange={(v) => set("currency", v)}><SelectTrigger data-testid="expense-currency-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="IDR">IDR (Rp)</SelectItem><SelectItem value="AUD">AUD</SelectItem></SelectContent></Select>
            </Field>
            {form.currency === "AUD" && <Field label="Kurs"><Input value={form.exchange_rate} onChange={(e) => set("exchange_rate", e.target.value)} /></Field>}
            <div className="sm:col-span-2"><Field label="Deskripsi"><Input value={form.description} onChange={(e) => set("description", e.target.value)} /></Field></div>
          </div>
          <ImageUpload value={form.receipt} onChange={(b) => set("receipt", b)} label="Struk" testId="expense-receipt-upload" />
          <DialogFooter><Button variant="outline" onClick={() => setOpen(false)}>Batal</Button><Button onClick={save} data-testid="expense-save-button">Simpan</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <ArchiveDialog open={!!archiveTarget} onOpenChange={(v) => { if (!v) setArchiveTarget(null); }}
        title="Arsipkan Pengeluaran?"
        description={`${archiveTarget?.category || ""} · ${formatIDR(archiveTarget?.amount_idr || 0)} akan diarsipkan dan disembunyikan dari daftar. Dapat dipulihkan dari Kotak Sampah.`}
        onConfirm={doArchive} testId="expense-archive" />
    </div>
  );
}
